import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Download, FileText, Loader2, Send } from "lucide-react";
import type { IvaPeriodBoleto } from "@fiscalo/shared-types";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  getAccountantBoletoSession,
  startAccountantBoleto,
} from "@/services/dashboardService";

/** YYYY-MM-DD(THH...) → DD/MM/YYYY, timezone-safe. */
function uyDate(iso: string): string {
  const [y, m, d] = (iso ?? "").slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

function money(n: number): string {
  return n.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export interface BoletoPagoCardProps {
  tenantId: string;
  /** IVA obligation for the period; null disables generation. */
  obligationId: string | null;
  /** Net IVA to pay for the period (UYU); ≤ 0 disables generation. */
  aPagarUyu: number;
  /** Attached boleto, when already generated. */
  boleto: IvaPeriodBoleto | null;
  /** Called after a successful generation so the parent can refetch. */
  onGenerated: () => void | Promise<void>;
}

/**
 * "Boleto de pago" card for an IVA period: generate (kicks off the DGI
 * automation, which auto-solves the CAPTCHA, then polls for the result),
 * download, and send-to-client (disabled until testing). Used on both the
 * accountant IVA-period page and the tenant-detail period summary.
 */
export function BoletoPagoCard({
  tenantId,
  obligationId,
  aPagarUyu,
  boleto,
  onGenerated,
}: BoletoPagoCardProps): JSX.Element {
  const { t } = useTranslation();
  const [generating, setGenerating] = useState(false);
  const cancelPollRef = useRef(false);

  useEffect(
    () => () => {
      cancelPollRef.current = true;
    },
    [],
  );

  const handleGenerate = async (): Promise<void> => {
    if (!obligationId || generating) return;
    setGenerating(true);
    cancelPollRef.current = false;
    const sleep = (ms: number): Promise<void> =>
      new Promise((resolve) => setTimeout(resolve, ms));
    try {
      const { sessionId } = await startAccountantBoleto(tenantId, obligationId);
      const deadline = Date.now() + 3 * 60 * 1000; // give the DGI tool up to 3 min
      while (!cancelPollRef.current && Date.now() < deadline) {
        await sleep(5000);
        if (cancelPollRef.current) return;
        let session: { status: string | null } | null = null;
        try {
          session = await getAccountantBoletoSession(sessionId);
        } catch {
          continue; // transient; keep polling
        }
        if (session.status === "success") {
          await onGenerated();
          toast.success(
            t("accountantIvaPeriod.boleto.generated", "Boleto generado."),
          );
          return;
        }
        if (
          session.status === "failed" ||
          session.status === "awaiting_captcha" ||
          session.status === "wrong_captcha"
        ) {
          toast.error(
            t(
              "accountantIvaPeriod.boleto.failed",
              "No se pudo generar el boleto automáticamente. Probá de nuevo.",
            ),
          );
          return;
        }
      }
      if (!cancelPollRef.current) {
        toast.error(
          t(
            "accountantIvaPeriod.boleto.timeout",
            "La generación está demorando. Revisá en unos minutos.",
          ),
        );
      }
    } catch {
      toast.error(
        t(
          "accountantIvaPeriod.boleto.startError",
          "No pudimos iniciar la generación del boleto.",
        ),
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card>
      <CardContent className="py-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-[var(--fx-ink-900)]">
              {t("accountantIvaPeriod.boleto.title", "Boleto de pago")}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("accountantIvaPeriod.boleto.subtitle", "IVA a pagar del período")}:{" "}
              <span className="tabular-nums">$U {money(aPagarUyu)}</span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {boleto ? (
              <>
                <Button asChild variant="outline" size="sm">
                  <a href={boleto.fileUrl} target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4" />
                    <span className="ml-1.5">
                      {t("accountantIvaPeriod.boleto.download", "Descargar boleto")}
                    </span>
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled
                  title={t(
                    "accountantIvaPeriod.boleto.sendDisabled",
                    "Disponible próximamente",
                  )}
                >
                  <Send className="h-4 w-4" />
                  <span className="ml-1.5">
                    {t("accountantIvaPeriod.boleto.send", "Enviar al cliente")}
                  </span>
                </Button>
              </>
            ) : aPagarUyu > 0 && obligationId ? (
              <Button
                size="sm"
                onClick={() => void handleGenerate()}
                disabled={generating}
              >
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                <span className="ml-1.5">
                  {generating
                    ? t("accountantIvaPeriod.boleto.generating", "Generando boleto…")
                    : t(
                        "accountantIvaPeriod.boleto.generate",
                        "Generar boleto de pago",
                      )}
                </span>
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                {aPagarUyu <= 0
                  ? t(
                      "accountantIvaPeriod.boleto.nothingToPay",
                      "Sin saldo a pagar este período",
                    )
                  : t(
                      "accountantIvaPeriod.boleto.noObligation",
                      "Sin obligación de IVA para el período",
                    )}
              </span>
            )}
          </div>
        </div>
        {boleto ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {t(
              "accountantIvaPeriod.boleto.meta",
              "Boleto N° {{number}} · generado el {{date}}",
              {
                number: boleto.number ?? "—",
                date: uyDate(boleto.uploadedAt),
              },
            )}
          </p>
        ) : generating ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {t(
              "accountantIvaPeriod.boleto.generatingHint",
              "Generando el boleto en la DGI. Esto puede tardar uno o dos minutos.",
            )}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
